/**
 * The cleaned file — what a visitor actually came for.
 *
 * ⚠️ **Until this existed the local journey had no ending.** Upload → map → profile → match →
 * decide → *nothing*: someone could confirm 500 duplicates and leave with a screen. Reviewing
 * duplicates is not the job; getting a usable file back is.
 *
 * ─────────────────────────────────────────────────────────────────────────────────────────────
 * WHAT GOES IN THE FILE, AND WHY EACH CHOICE
 *
 * One row per **surviving entity plus every unmerged record**. A merged cluster contributes one row
 * (its golden record); everything untouched passes through unchanged.
 *
 * **Only APPLIED merges collapse.** A cluster the engine found but nobody applied exports as its
 * separate records, because exporting a speculative merge would ship a decision no human made —
 * M8's business rule 2, and the same reasoning here as in the hosted export.
 *
 * **Lineage columns are mandatory, not optional** (BR-12). Every row carries
 * `dedupr_source_ids` and `dedupr_merged_count`, so a row in the customer's downstream system can be
 * traced back to the records it came from. An export that loses that is a file nobody can audit, and
 * "which three records became this one" is the first question anyone asks.
 *
 * **The original columns are preserved**, not replaced with our canonical field names. A file whose
 * headers changed is a file someone has to re-map in whatever they load it into next — the export
 * exists to be usable, not to be tidy.
 */
import type { FieldResolution, MatchRecord } from '@dedupr/match-core';
import type { StoredGolden, StoredRecord } from './store.js';

/** Columns appended to every row. Prefixed, so they cannot collide with a customer's own headers. */
export const LINEAGE_COLUMNS = ['dedupr_source_ids', 'dedupr_merged_count', 'dedupr_contested_fields'] as const;

/**
 * A row id as the visitor knows it. Store ids are namespaced `${sourceId}:${their own row id}`
 * (Z-0), and the source-id half means nothing outside this device — lineage that says
 * `1f46e4ad-…:70002` is untraceable in the file it points at, where `70002` is a row they can find.
 *
 * ⚠️ Stripped by the EXACT source ids the input's records carry, never by guessing at the id's
 * shape — a regex version of this shipped for ten minutes and only worked when the source id
 * happened to be a UUID.
 */
export function rowIdStripper(records: readonly StoredRecord[]): (id: string) => string {
  const prefixes = [...new Set(records.map((r) => `${r.source_id}:`))];
  return (id) => {
    for (const p of prefixes) if (id.startsWith(p)) return id.slice(p.length);
    return id;
  };
}

export interface ExportInput {
  /** The original header order, so the file reads like the one that was uploaded. */
  headers: readonly string[];
  /** Original rows, keyed by record id — the values as they arrived. */
  rows: ReadonlyMap<string, readonly string[]>;
  /** Which original column each canonical field came from, for writing survivors back in place. */
  columnOf: Readonly<Record<string, number | undefined>>;
  records: readonly StoredRecord[];
  /** Applied merges only. A rolled-back one is not applied. */
  golden: readonly StoredGolden[];
}

/** RFC 4180: quote only when needed, so the file stays readable. Shared with the report builders. */
export function csvCell(value: unknown): string {
  const s = value === null || value === undefined ? '' : String(value);
  return /[",\n\r]/.test(s) ? `"${s.replaceAll('"', '""')}"` : s;
}

/**
 * Build the cleaned CSV.
 *
 * Returns the text rather than triggering a download, so it is testable without a DOM — the download
 * is one line in the caller.
 */
export function buildCleanedCsv(input: ExportInput): string {
  const applied = input.golden.filter((g) => !g.rolled_back_at);
  const ownRowId = rowIdStripper(input.records);

  /* Every record that a merge consumed. Built first so the pass-through loop can skip them in one
     lookup rather than scanning the merge list per record. */
  const consumed = new Set<string>();
  for (const g of applied) for (const id of g.members) consumed.add(id);

  const headers = [...input.headers, ...LINEAGE_COLUMNS];
  const lines: string[] = [headers.map(csvCell).join(',')];

  for (const g of applied) {
    /* The surviving row starts as a COPY of its first member's original row, then has the surviving
       values written over the mapped columns. That preserves every column the matcher never looked
       at — an export that dropped the 40 fields we do not score would be worse than the input. */
    const template = input.rows.get(g.members[0]!) ?? [];
    const row = [...template];
    const record = g.record as MatchRecord & Record<string, unknown>;

    for (const [field, index] of Object.entries(input.columnOf)) {
      if (index === undefined) continue;
      const value = record[field];
      // `null` means no member had a value; write an empty cell rather than the string "null".
      if (value !== undefined) row[index] = value === null ? '' : String(value);
    }

    const contested = (g.resolutions as FieldResolution[] | undefined)?.filter((r) => r.contested) ?? [];
    lines.push(
      [
        ...headers.slice(0, input.headers.length).map((_, i) => csvCell(row[i] ?? '')),
        csvCell(g.members.map(ownRowId).join(' ')),
        csvCell(g.members.length),
        /* Which fields disagreed, named. A downstream reader deciding whether to trust a row wants
           to know it was assembled from conflicting values — a merged flag alone does not say that,
           and the discarded values themselves belong in the audit trail rather than in a data file. */
        csvCell(contested.map((r) => r.field).join(' ')),
      ].join(',')
    );
  }

  for (const record of input.records) {
    if (consumed.has(record.id)) continue;
    const row = input.rows.get(record.id) ?? [];
    lines.push(
      [
        ...input.headers.map((_, i) => csvCell(row[i] ?? '')),
        // Its own id: a row that was never merged still has provenance, and a blank would read as
        // "unknown origin" rather than "came from exactly one record".
        csvCell(ownRowId(record.id)),
        csvCell(1),
        csvCell(''),
      ].join(',')
    );
  }

  return lines.join('\n') + '\n';
}

export interface ExportSummary {
  rows: number;
  merged: number;
  recordsRemoved: number;
}

/** What to tell the person before they download — counts, so the file is not a surprise. */
export function summarise(input: ExportInput): ExportSummary {
  const applied = input.golden.filter((g) => !g.rolled_back_at);
  const consumed = applied.reduce((n, g) => n + g.members.length, 0);
  return {
    rows: applied.length + (input.records.length - consumed),
    merged: applied.length,
    /* The honest framing: N records became M rows. "Removed" counts what collapsed, not what was
       deleted — nothing is deleted, and the originals are still on the device. */
    recordsRemoved: consumed - applied.length,
  };
}

/** Trigger a download of any text artifact. Separated from the builders so they need no DOM. */
export function downloadText(filename: string, text: string, mime: string): void {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  link.click();
  /* Revoked, or every export leaks its file for the lifetime of the tab — which on a 50,000-row
     export is tens of megabytes held for no reason. */
  URL.revokeObjectURL(url);
}

/** Trigger the CSV download. Kept as the named thing callers reach for. */
export function downloadCsv(filename: string, text: string): void {
  downloadText(filename, text, 'text/csv;charset=utf-8');
}
